/**
 * utils/index.ts — Thin adapter layer
 *
 * Re-exports auth-related functions from @nestingale/auth so that existing imports
 * (`import { ... } from '@/utils'`) continue to work.
 *
 * UI-specific utilities (Brands, DeviceDetector, etc.) are kept here.
 */

'use client'

import { useEffect, useState } from 'react';

// ─── Re-exports from @nestingale/auth ───────────────────────────────────────────────
export {
  getCurrentSession,
  getLoggedInName,
  getLoggedInEmailAddress,
  CART_TOKEN,
  GCART_TOKEN,
  sanitizeNextParam,
  gtmPageOnloadTracking,
  gtmClickTracking,
  trackItem,
  navigateWithHistory,
  navigateAndReplaceHistory,
} from '@nestingale/auth';

// Re-export getEitherCustIdFromLs (used in GTM tracking)
export { getEitherCustIdFromLs } from '@nestingale/auth';

// TrackInfo interface used by header menu tracking
export interface TrackInfo {
  event: string;
  name?: string;
}

// ─── UI Utilities (app-specific) ──────────────────────────────────────────────

interface BrandDetails {
  name: string;
  logoUrl: string;
}

export const Brands = {
  Nestingale: { name: '', logoUrl: null },
  Ammerica: { name: 'aamerica', logoUrl: 'https://d221a61rb87vel.cloudfront.net/catalog-assets/vendor/logos/aamerica-logo.svg' },
  Flexsteel: { name: 'flexsteel', logoUrl: 'https://d221a61rb87vel.cloudfront.net/catalog-assets/vendor/logos/flexsteel_logo.png' },
  Homestyles: { name: 'homestyles', logoUrl: 'https://d221a61rb87vel.cloudfront.net/catalog-assets/vendor/logos/homestyles-logo-B-200x80_200x80.jpg' },
  addison: { name: 'addison', logoUrl: 'https://d221a61rb87vel.cloudfront.net/catalog-assets/vendor/logos/Addison House.jpeg' },
  moes: { name: 'moes', logoUrl: 'https://d221a61rb87vel.cloudfront.net/catalog-assets/vendor/logos/MoesLogo.jpeg' },
  stevesilver: { name: 'stevesilver', logoUrl: 'https://d221a61rb87vel.cloudfront.net/catalog-assets/vendor/logos/SteveSilverLogo.jpg' },
} as any;

export function navigateWithOutPreviousHistory(router: any, path: any) {
  router.push(path, undefined, { shallow: true, replace: true });
}

export function getBaseName() {
  const pathname = typeof window !== 'undefined' ? new URL(window.location.href).pathname : '/';
  const brands = Object.keys(Brands);
  let baseBrand = Brands.Nestingale;
  for (const value of brands) {
    if (pathname.indexOf(`/${Brands[value].name.toLowerCase()}/`) >= 0) {
      baseBrand = Brands[value];
      break;
    }
  }
  return baseBrand;
}

export const DeviceDetector = () => {
  const [deviceType, setDeviceType] = useState('');

  useEffect(() => {
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(
        navigator.userAgent
      )
    ) {
      setDeviceType('Mobile');
    } else {
      setDeviceType('Desktop');
    }
  }, []);

  return deviceType;
};

export const isIos = (): boolean => {
  return DeviceDetector() === 'Mobile'
    ? /iPhone|iPad|iPod/i.test(navigator.userAgent)
    : (false as boolean);
};

export function isCartPage(isFromHeader: boolean = false) {
  const isWindowPresent = typeof window !== 'undefined';
  if (!isWindowPresent) return true;
  if (isWindowPresent) {
    const pageUrl = window.location.href;
    if (pageUrl.indexOf('cart') >= 0) return true;
  }
  return false;
}

export function isHeaderNotActiveOnPage(isFromHeader: boolean = false) {
  const isWindowPresent = typeof window !== 'undefined';
  if (!isWindowPresent) return true;
  return false;
}

function componentToHex(c: any) {
  var hex = c.toString(16);
  return hex.length == 1 ? '0' + hex : hex;
}

export function rgbToHex(r: any, g: any, b: any) {
  return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
}

export function deepEqual(objA: any, objB: any) {
  if (objA === objB) return true;
  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null)
    return false;
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) return false;
  for (let key of keysA) {
    if (!keysB.includes(key) || !deepEqual(objA[key], objB[key])) return false;
  }
  return true;
}

export function checkIfImageExists(url: string, callback: any) {
  const img = new Image();
  img.src = url;
  if (img.complete) {
    callback(true);
  } else {
    img.onload = () => callback(true);
    img.onerror = () => callback(false);
  }
}

export function dataURItoBlob(dataURI: string) {
  var byteString;
  if (dataURI.split(',')[0].indexOf('base64') >= 0) byteString = atob(dataURI.split(',')[1]);
  else byteString = unescape(dataURI.split(',')[1]);
  var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  var ia = new Uint8Array(byteString.length);
  for (var i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ia], { type: mimeString });
}

export function htmlEncode(str: string) {
  var buf = [];
  for (var i = str.length - 1; i >= 0; i--) {
    buf.unshift(['&#', str.charCodeAt(i), ';'].join(''));
  }
  return buf.join('');
}

export function HtmlEncode(s: string) {
  function encodeHTMLmapper(ch: string) {
    const HTMLCharMap: { [key: string]: string } = {
      '&': '&amp;',
      "'": '&#39;',
      '"': '&quot;',
      '<': '&lt;',
      '>': '&gt;',
      '\\': '&#x5c;',
      '`': '&#x60;',
      ':': '&#58;',
    };
    return HTMLCharMap[ch];
  }
  return s.replace(/[&"'<>\\`:]/g, encodeHTMLmapper);
}
